import { useCallback, useSyncExternalStore } from "react";
import { useAuthStore } from "@/store/auth.store";

// Whether the pinned "Pre-chart" section of the visits list is folded
// (clinician request 05/11), remembered per account in this browser. The
// sidebar and the visits page share it, and fold together.

const KEY_PREFIX = "rosc.visits.preChartCollapsed.";
const listeners = new Set<() => void>();

const read = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  const onStorage = (event: StorageEvent): void => {
    if (event.key?.startsWith(KEY_PREFIX)) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

export const preChartSectionKey = (account: string | null | undefined) =>
  `${KEY_PREFIX}${account || "signed-out"}`;

export const usePreChartSectionCollapsed = (): [boolean, () => void] => {
  const account = useAuthStore((state) => state.userData?.public_id);
  const key = preChartSectionKey(account);
  const collapsed = useSyncExternalStore(
    subscribe,
    () => read(key),
    () => false,
  );
  const toggle = useCallback(() => {
    try {
      if (read(key)) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, "1");
    } catch {
      // Storage blocked: nothing is remembered.
    }
    for (const listener of listeners) listener();
  }, [key]);
  return [collapsed, toggle];
};
